/* WinOptIn — Étape 1 : prénom + téléphone + consentement (inscription mensuelle).
   Étape 2 : liste des tickets instantanés disponibles (format « Ticket validé —
   c'est à vous », avec marque + info du lot). Un clic ouvre un popup ScratchDialog
   qui gratte CE ticket. Chaque grattage = un ticket ; le client peut en gratter
   plusieurs différents. Textes issus de `texts` (éditables via l'admin). */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatErr, PASSENGER_TIMEOUT_MS } from "@/lib/api";
import ScratchDialog from "@/components/scan/ScratchDialog";
import { readableOn, sponsorGradient, tint } from "@/lib/color";
import { CheckCircle2, Clock, Loader2, MapPin, Phone, Shield, User, Ticket, ChevronRight, Sparkles } from "lucide-react";
import { fillTemplate, DEFAULT_TEXTS } from "@/lib/defaults";
import { loadClient, saveClient, clearClient } from "@/lib/clientCache";

/* Plan de numérotation CI (2021) : mobiles à 10 chiffres — 01 Moov, 05 MTN, 07 Orange.
   L'indicatif +225 / 00225 est toléré puis retiré. */
const normalizePhoneCI = (raw) => {
  let d = (raw || "").replace(/\D/g, "");
  if (d.startsWith("00225")) d = d.slice(5);
  else if (d.startsWith("225")) d = d.slice(3);
  return d;
};
const isValidCIMobile = (raw) => {
  const d = normalizePhoneCI(raw);
  return d.length === 10 && ["01", "05", "07"].includes(d.slice(0, 2));
};

/* Infos device du téléphone, envoyées avec le ticket de grattage
   (l'IP est relevée côté serveur sur la même requête). */
const collectDeviceInfo = () => {
  try {
    const nav = navigator;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    const uaData = nav.userAgentData;
    return {
      userAgent: nav.userAgent,
      platform: uaData?.platform || nav.platform || null,
      mobile: uaData?.mobile ?? null,
      brands: uaData?.brands?.map((b) => `${b.brand} ${b.version}`) || null,
      language: nav.language,
      languages: nav.languages ? [...nav.languages] : null,
      screen: `${window.screen.width}x${window.screen.height}`,
      pixelRatio: window.devicePixelRatio || 1,
      touchPoints: nav.maxTouchPoints ?? null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      connectionType: conn?.effectiveType || null,
      deviceMemory: nav.deviceMemory ?? null,
      hardwareConcurrency: nav.hardwareConcurrency ?? null,
      cookiesEnabled: nav.cookieEnabled,
    };
  } catch (_) {
    return { userAgent: navigator.userAgent };
  }
};

/* Carte-ticket dans la liste — même esprit visuel que le ticket révélé
   (talon perforé + corps), avec marque du sponsor et infos du lot. */
export function TicketCard({ ticket, texts, onOpen }) {
  const st = ticket.status;
  const clickable = st === "available";
  const sponsor = ticket.sponsor;

  // Le ticket prend les couleurs du sponsor de CE tirage ; à défaut, celles de
  // la plateforme. Le dégradé n'existe que si le sponsor a une couleur
  // secondaire, sinon on reste sur un aplat.
  const primary = sponsor?.primaryColor;
  const secondary = sponsor?.secondaryColor;
  const accent = primary || "var(--color-brand)";
  const stubBg = sponsorGradient(primary, secondary) || "var(--color-brand)";
  const onAccent = readableOn(primary);

  const hours = Math.max(1, Math.ceil((ticket.retryAfterSeconds || 0) / 3600));
  const badge = {
    available: { label: "À gratter", style: { background: accent, color: onAccent } },
    cooldown: { label: `Revenez dans ${hours}h`, cls: "text-muted-foreground bg-muted" },
    won: { label: "Gagné 🎉", cls: "text-emerald-700 bg-emerald-100" },
    exhausted: { label: "Épuisé", cls: "text-muted-foreground bg-muted" },
  }[st] || { label: st, cls: "bg-muted text-muted-foreground" };

  return (
    <motion.button
      type="button"
      onClick={clickable ? onOpen : undefined}
      disabled={!clickable}
      whileTap={clickable ? { scale: 0.985 } : undefined}
      data-testid={`ticket-card-${ticket.id}`}
      data-status={st}
      className={`relative w-full flex items-stretch overflow-hidden rounded-2xl border-2 text-left transition-shadow ${
        clickable ? "ticket-shimmer hover:shadow-lg cursor-pointer" : "opacity-60 cursor-not-allowed grayscale"
      }`}
      style={{
        borderColor: clickable ? accent : "hsl(var(--border))",
        background: clickable ? tint(primary, 0.06) || "hsl(var(--card))" : "hsl(var(--card))",
      }}
    >
      {/* Talon sponsor */}
      <div
        className="relative w-[62px] flex-shrink-0 grid place-items-center"
        style={{ background: clickable ? stubBg : "hsl(var(--muted))", color: onAccent }}
      >
        {sponsor?.logoUrl ? (
          <img
            src={sponsor.logoUrl}
            alt={sponsor.name}
            className="h-11 w-11 object-contain rounded-lg bg-white p-1 shadow-sm"
          />
        ) : (
          <Ticket className="h-6 w-6" />
        )}
        {/* Ligne de perforation + encoches : donne la lecture « vrai ticket » */}
        <div
          className="absolute right-0 top-0 bottom-0 w-px"
          style={{
            background:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.65) 0 4px, transparent 4px 9px)",
          }}
        />
        <span
          className="absolute -right-[7px] -top-[7px] h-3.5 w-3.5 rounded-full"
          style={{ background: "hsl(var(--card))" }}
        />
        <span
          className="absolute -right-[7px] -bottom-[7px] h-3.5 w-3.5 rounded-full"
          style={{ background: "hsl(var(--card))" }}
        />
      </div>

      {/* Corps */}
      <div className="flex-1 min-w-0 px-3.5 py-3">
        <div
          className="text-[10px] uppercase tracking-[0.18em] font-mono truncate"
          style={{ color: clickable && primary ? primary : undefined }}
        >
          {sponsor?.name || ticket.drawTitle || texts.sponsorBannerLabel}
        </div>
        <div className="font-heading font-bold text-sm leading-tight mt-0.5 line-clamp-2">
          {ticket.prizeDescription || ticket.drawTitle || "Lot surprise"}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls || ""}`}
            style={badge.style}
          >
            {st === "available" && <Sparkles className="h-2.5 w-2.5" />}
            {st === "cooldown" && <Clock className="h-2.5 w-2.5" />}
            {badge.label}
          </span>
          {clickable && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] font-semibold"
              style={{ color: accent }}
            >
              Gratter <ChevronRight className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
      </div>

      {/* Visuel du lot — la donnée existait déjà mais n'était pas affichée ici */}
      {ticket.prizeImageUrl && (
        <div className="flex-shrink-0 self-center pr-3">
          <img
            src={ticket.prizeImageUrl}
            alt=""
            className="h-14 w-14 rounded-xl object-cover border"
            style={{ borderColor: clickable ? tint(primary, 0.35) || "hsl(var(--border))" : "hsl(var(--border))" }}
          />
        </div>
      )}
    </motion.button>
  );
}

export default function WinOptIn({ scanEventId, sponsor, monthlySponsor, draw, instantDraws, texts: t, cacheDays = 7 }) {
  const texts = { ...DEFAULT_TEXTS, ...(t || {}) };
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [remember, setRemember] = useState(true); // mémoriser sur cet appareil
  const [showLegal, setShowLegal] = useState(false);
  const [state, setState] = useState("form"); // form | loading | tickets | error
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [locality, setLocality] = useState(null); // commune/ville servie par le back
  const [activeTicket, setActiveTicket] = useState(null);
  const [knownClient, setKnownClient] = useState(false); // identité reprise du cache

  // Inscription (mensuel) + récupération des tickets. Partagé entre la
  // soumission manuelle et la reprise auto depuis le cache. On rappelle
  // TOUJOURS /optin (les statuts des tickets changent avec le temps).
  const registerWith = async ({ firstName: fn, phone: ph, consent: cs, fromCache }) => {
    setError("");
    setState("loading");
    try {
      const { data } = await api.post(
        "/optin",
        {
          firstName: fn.trim(),
          phone: ph,
          consent: cs,
          scanEventId,
          device: collectDeviceInfo(),
        },
        { timeout: PASSENGER_TIMEOUT_MS }
      );
      setTickets(data.tickets || []);
      setLocality(data.locality || null);
      setKnownClient(!!fromCache);
      setState("tickets");
      // (re)mémorise et fait glisser la fenêtre TTL tant que le client est actif
      if (fromCache || remember) saveClient({ firstName: fn.trim(), phone: ph });
      return true;
    } catch (err) {
      if (fromCache) {
        // Échec sur des infos en cache → on repasse au formulaire pré-rempli.
        // Si le serveur les juge invalides (400), on purge le cache.
        if (err?.response?.status === 400) clearClient();
        setState("form");
      } else {
        setState("error");
        setError(formatErr(err));
      }
      return false;
    }
  };

  // Reprise auto de l'identité mémorisée (une seule fois au montage).
  const autoTried = useRef(false);
  useEffect(() => {
    if (autoTried.current || !scanEventId) return;
    autoTried.current = true;
    const cached = loadClient(cacheDays);
    if (cached) {
      setFirstName(cached.firstName);
      setPhone(cached.phone);
      setConsent(true);
      registerWith({ firstName: cached.firstName, phone: cached.phone, consent: true, fromCache: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanEventId]);

  // « Ce n'est pas vous ? » — oublie l'identité et réaffiche le formulaire.
  const forgetMe = () => {
    clearClient();
    setKnownClient(false);
    setTickets([]);
    setActiveTicket(null);
    setFirstName("");
    setPhone("");
    setConsent(false);
    setError("");
    setState("form");
  };

  // Rotation des sponsors de lots instantanés dans l'en-tête du formulaire.
  const instantSponsors = useMemo(
    () => (instantDraws || []).map((d) => d.sponsor).filter(Boolean),
    [instantDraws]
  );
  const [spIdx, setSpIdx] = useState(0);
  useEffect(() => {
    if (instantSponsors.length <= 1) return;
    const timer = setInterval(() => setSpIdx((i) => (i + 1) % instantSponsors.length), 4000);
    return () => clearInterval(timer);
  }, [instantSponsors.length]);
  const rotatingSponsor = instantSponsors.length ? instantSponsors[spIdx % instantSponsors.length] : sponsor;

  const foil = rotatingSponsor?.primaryColor || sponsor?.primaryColor || "var(--color-accent)";
  // Lot sans description (présenté par son visuel) → on retombe sur le titre du tirage.
  const prizeDesc = draw?.prizeDescription || draw?.drawTitle || "5000 FCFA Mobile Money";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (firstName.trim().length < 2) return setError("Prénom requis (min. 2 caractères).");
    if (!consent) return setError("Vous devez accepter les conditions pour participer.");
    if (!isValidCIMobile(phone))
      return setError("Numéro invalide — entrez un numéro mobile à 10 chiffres commençant par 01, 05 ou 07.");
    if (!remember) clearClient(); // respecte le refus de mémorisation
    await registerWith({ firstName, phone, consent, fromCache: false });
  };

  // Mise à jour du statut d'un ticket après grattage (ou blocage serveur).
  const onScratchResult = (ticketId, res) => {
    setTickets((ts) =>
      ts.map((t) => {
        if (t.id !== ticketId) return t;
        if (res.blocked) return { ...t, ...res.blocked };
        if (res.instantWin) return { ...t, status: "won" };
        return { ...t, status: "cooldown", retryAfterSeconds: 24 * 3600 };
      })
    );
  };

  // ---------- Étape 2 : liste des tickets ----------
  if (state === "tickets") {
    const available = tickets.filter((t) => t.status === "available").length;
    // Les tickets sont filtrés côté serveur sur la commune du scan (ou, à
    // défaut, la ville déduite de l'IP) : on le dit pour éviter le « pourquoi
    // mon cousin voit un autre lot ? ».
    const place = locality?.zoneName || locality?.cityName;
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="tickets-list"
        className="rounded-3xl border-2 border-border bg-card p-5"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5" style={{ color: "var(--color-brand)" }} />
          <h2 className="font-heading text-xl font-bold leading-tight">
            {texts.gateValidatedLabel || "Ticket validé — c'est à vous !"}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {tickets.length === 0
            ? "Aucun ticket n'est proposé dans votre commune pour le moment. Revenez bientôt !"
            : available > 0
            ? "Choisissez un ticket et grattez-le. Vous pouvez en gratter plusieurs différents."
            : "Vous avez gratté tous vos tickets disponibles. Revenez plus tard pour retenter votre chance !"}
        </p>
        {place && tickets.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            Offres disponibles à {place}
          </p>
        )}

        {knownClient && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs">
            <span className="text-muted-foreground truncate">
              Vous jouez en tant que <span className="font-semibold text-foreground">{firstName}</span>
            </span>
            <button
              type="button"
              onClick={forgetMe}
              data-testid="optin-forget"
              className="flex-shrink-0 underline underline-offset-2 text-foreground"
            >
              Ce n'est pas vous&nbsp;?
            </button>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {tickets.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Aucun ticket instantané disponible pour le moment. Vous êtes bien inscrit au tirage mensuel.
            </div>
          )}
          {tickets.map((tk) => (
            <TicketCard key={tk.id} ticket={tk} texts={texts} onOpen={() => setActiveTicket(tk)} />
          ))}
        </div>

        {activeTicket && (
          <ScratchDialog
            ticket={activeTicket}
            scanEventId={scanEventId}
            phone={phone}
            firstName={firstName.trim()}
            texts={texts}
            foilFallback={typeof foil === "string" && foil.startsWith("#") ? foil : "#FFC247"}
            onClose={() => setActiveTicket(null)}
            onResult={onScratchResult}
          />
        )}
      </motion.div>
    );
  }

  // ---------- Étape 1 : formulaire ----------
  return (
    <motion.form
      onSubmit={handleSubmit}
      data-testid="optin-form"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-3xl border-2 border-border bg-card p-5"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {/* Sponsor row — rotates through instant-prize sponsors, falls back to monthly */}
      {(() => {
        const shown = rotatingSponsor || monthlySponsor;
        if (!shown) return null;
        return (
          <div className="mb-3 flex items-center justify-between gap-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={shown.id || shown.name}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted text-[10px] uppercase tracking-[0.18em] font-mono"
              >
                <span className="pulse-dot" />
                {texts.sponsorBannerLabel} <span className="font-semibold">{shown.name}</span>
              </motion.div>
            </AnimatePresence>
            {shown.logoUrl && (
              <img
                src={shown.logoUrl}
                alt={shown.name}
                className="h-8 max-w-[100px] object-contain rounded-md bg-white/60 p-1 border border-border"
                data-testid="header-sponsor-logo"
              />
            )}
          </div>
        );
      })()}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-2xl font-bold leading-tight">{texts.optInTitle}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {fillTemplate(texts.optInSubtitle, { prize: prizeDesc })}
          </p>
        </div>
      </div>

      {/* First name */}
      <label className="block mt-5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Prénom
      </label>
      <div className="mt-1.5 flex items-stretch rounded-xl border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring">
        <span className="flex items-center gap-1 px-3 text-sm font-mono text-muted-foreground bg-muted">
          <User className="h-4 w-4" />
        </span>
        <input
          type="text"
          name="firstName"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Aïcha"
          data-testid="optin-firstname-input"
          className="flex-1 px-3 py-3 h-12 bg-transparent outline-none text-base"
          autoComplete="given-name"
          maxLength={40}
          required
        />
      </div>

      {/* Phone */}
      <label className="block mt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Téléphone
      </label>
      {(() => {
        const digits = normalizePhoneCI(phone);
        const phoneValid = isValidCIMobile(phone);
        const phoneInvalid = digits.length >= 10 && !phoneValid;
        return (
          <>
            <div
              className={`mt-1.5 flex items-stretch rounded-xl border bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring ${
                phoneInvalid ? "border-red-400" : "border-input"
              }`}
            >
              <span className="flex items-center gap-1 px-3 text-sm font-mono text-muted-foreground bg-muted">
                <Phone className="h-4 w-4" /> +225
              </span>
              <input
                inputMode="tel"
                type="tel"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s+]/g, ""))}
                placeholder="01 23 45 67 89"
                data-testid="optin-phone-input"
                className="flex-1 px-3 py-3 h-12 bg-transparent outline-none text-base font-mono tracking-wide"
                autoComplete="tel"
                maxLength={20}
                required
              />
              {phoneValid && (
                <span className="flex items-center px-3" data-testid="optin-phone-valid">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </span>
              )}
            </div>
            {phoneInvalid && (
              <div
                className="mt-1.5 text-[11px] leading-relaxed"
                style={{ color: "var(--color-error, #D92D20)" }}
                data-testid="optin-phone-hint"
              >
                Numéro invalide : 10 chiffres commençant par 01, 05 ou 07 (ex : 07 07 12 34 56).
              </div>
            )}
          </>
        );
      })()}

      {/* Consent */}
      <label className="mt-4 flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="optin-consent-checkbox"
          className="mt-1 h-5 w-5 accent-[var(--color-brand)] cursor-pointer"
        />
        <span className="text-xs leading-relaxed text-muted-foreground">
          {texts.consentText}{" "}
          <button
            type="button"
            onClick={() => setShowLegal((s) => !s)}
            className="underline underline-offset-2 text-foreground"
          >
            En savoir plus
          </button>
        </span>
      </label>
      <AnimatePresence>
        {showLegal && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-xl bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground">
              {texts.legalDetail}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div
          role="alert"
          data-testid="optin-error"
          className="mt-3 text-sm rounded-lg px-3 py-2"
          style={{ color: "var(--color-error, #D92D20)", background: "rgba(217, 45, 32, 0.08)" }}
        >
          {error}
        </div>
      )}

      {/* Mémorisation sur l'appareil (fenêtre glissante, purge auto à l'expiration) */}
      <label className="mt-4 flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          data-testid="optin-remember-checkbox"
          className="h-4 w-4 accent-[var(--color-brand)] cursor-pointer"
        />
        Rester connecté sur cet appareil ({cacheDays} j)
      </label>

      <button
        type="submit"
        disabled={state === "loading"}
        data-testid="optin-submit-btn"
        className="mt-5 w-full h-12 rounded-full font-heading font-semibold text-base inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
        style={{ background: "var(--color-brand-fill)", color: "#fff", boxShadow: "0 8px 20px rgba(23, 190, 26, 0.35)" }}
      >
        {state === "loading" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Inscription…
          </>
        ) : (
          <>{texts.optInCta}</>
        )}
      </button>

      <div className="mt-3 flex items-center gap-2 justify-center text-[11px] text-muted-foreground">
        <Shield className="h-3.5 w-3.5" />
        {`Conforme Loi 2013-450 — ARTCI Côte d'Ivoire`}
      </div>
    </motion.form>
  );
}
